import type { MagazineArticle, MagazineArticleFilters, MagazineFilters, MagazineLicense, MagazineSection } from '@titularis/api-client';

export const DESCRICAO_DA_REVISTA = 'Artigos jurídicos de alto interesse da comunidade das serventias extrajudiciais e de concurseiros.';
export const ARTIGOS_POR_PAGINA = 24;
export const SECOES_DA_REVISTA: Record<MagazineSection, string> = {
  article: 'Artigo', review: 'Resenha', 'case-note': 'Comentário de jurisprudência',
};
export const LICENCAS_DA_REVISTA: Record<MagazineLicense, { nome: string; url: string }> = {
  'cc-by': { nome: 'CC BY 4.0', url: 'https://creativecommons.org/licenses/by/4.0/' },
  'cc-by-nc': { nome: 'CC BY-NC 4.0', url: 'https://creativecommons.org/licenses/by-nc/4.0/' },
  'cc-by-nc-nd': { nome: 'CC BY-NC-ND 4.0', url: 'https://creativecommons.org/licenses/by-nc-nd/4.0/' },
  'cc-by-sa': { nome: 'CC BY-SA 4.0', url: 'https://creativecommons.org/licenses/by-sa/4.0/' },
};

export interface FiltrosDaRevista {
  termo?: string;
  keyword?: string;
  ano?: string;
  edicao?: string;
  pagina?: number;
}

/** A URL digitada à mão passa pelo mesmo corte no navegador e na função da Vercel. */
export function lerFiltrosDaRevista(busca: URLSearchParams): FiltrosDaRevista {
  const ano = busca.get('ano') ?? '';
  const pagina = Number(busca.get('pagina'));
  return {
    termo: busca.get('termo')?.trim().slice(0, 200) || undefined,
    keyword: busca.get('keyword')?.trim().slice(0, 200) || undefined,
    ano: /^\d{4}$/.test(ano) && Number(ano) >= 1990 && Number(ano) <= 2100 ? ano : undefined,
    edicao: busca.get('edicao')?.slice(0, 120) || undefined,
    pagina: Number.isSafeInteger(pagina) && pagina > 0 ? pagina : 1,
  };
}

export const consultaDaRevista = (filtros: FiltrosDaRevista): MagazineArticleFilters => ({
  termo: filtros.termo, keyword: filtros.keyword, year: filtros.ano ? Number(filtros.ano) : undefined,
  issue: filtros.edicao, page: filtros.pagina ?? 1, pageSize: ARTIGOS_POR_PAGINA,
});

export const revistaFiltrada = (filtros: FiltrosDaRevista): boolean =>
  Boolean(filtros.termo || filtros.keyword || filtros.ano || filtros.edicao);

export function tituloDaBuscaDaRevista(filtros: FiltrosDaRevista, facetas?: MagazineFilters): string {
  const edicao = facetas?.edicoes.find((item) => item.slug === filtros.edicao)?.label;
  const recortes = [
    filtros.termo ? `termo “${filtros.termo}”` : null,
    filtros.keyword ? `palavra-chave “${filtros.keyword}”` : null,
    filtros.ano ? `ano ${filtros.ano}` : null,
    filtros.edicao ? `edição ${edicao ?? filtros.edicao}` : null,
  ].filter(Boolean);
  return `Resultados para ${recortes.join(' · ')}`;
}

/** A busca continua compartilhável; só o canônico omite o termo, como nas Provas. */
export function enderecoDaRevista(origem: string, filtros: FiltrosDaRevista = {}, comTermo = false): string {
  const busca = new URLSearchParams();
  if (comTermo && filtros.termo) busca.set('termo', filtros.termo);
  if (filtros.keyword) busca.set('keyword', filtros.keyword);
  if (filtros.ano) busca.set('ano', filtros.ano);
  if (filtros.edicao) busca.set('edicao', filtros.edicao);
  if ((filtros.pagina ?? 1) > 1) busca.set('pagina', String(filtros.pagina));
  return `${origem}/revista${busca.size ? `?${busca}` : ''}`;
}

export const recortesDaRevista = (origem: string, facetas: MagazineFilters) => [
  { titulo: 'Por palavra-chave', links: facetas.keywords.map((keyword) => ({ rotulo: keyword, url: enderecoDaRevista(origem, { keyword }) })) },
  { titulo: 'Por ano', links: facetas.anos.map((ano) => ({ rotulo: String(ano), url: enderecoDaRevista(origem, { ano: String(ano) }) })) },
  { titulo: 'Por edição', links: facetas.edicoes.map((edicao) => ({ rotulo: edicao.label, url: enderecoDaRevista(origem, { edicao: edicao.slug }) })) },
].filter((grupo) => grupo.links.length > 0);

/** A API omite meses vazios; completar a janela evita aproximar meses distantes no gráfico. */
export function mesesDeDownloads(serie: MagazineArticle['downloads']['serie'], agora: Date) {
  const totais = new Map(serie.map((item) => [item.mes, item.downloads]));
  return Array.from({ length: 12 }, (_, indice) => {
    const data = new Date(Date.UTC(agora.getUTCFullYear(), agora.getUTCMonth() - 11 + indice, 1));
    const mes = data.toISOString().slice(0, 7);
    const rotulo = new Intl.DateTimeFormat('pt-BR', { month: 'short', timeZone: 'UTC' }).format(data);
    return { mes, rotulo, downloads: totais.get(mes) ?? 0 };
  });
}
